import { useEffect, useState } from 'preact/hooks';
import { getRecord, getRecordFileUrl, getRecordWithLinks, type RecordRow } from '../lib/api';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';

import { LINK, otherEnd, AA } from '../lib/auditionSpread';
import './farSide.css';

// A5 — THE SHOOT DAY. THE 5:40am READ.
//
// ===================================================================
// THIS IS NOT A FORM, AND THAT IS THE ENTIRE DESIGN
// ===================================================================
// Every other screen in this product is read at a kitchen table with time to
// think. This one is read in a dark kitchen, standing up, holding a coffee,
// twenty minutes before leaving the house — and the parent is looking for
// exactly ONE number.
//
// So the card is built around that number and nothing else competes with it.
// `pickup_at` is the largest type on the screen by a wide margin and it gets
// BIGGER on a phone, not smaller, because the phone is the device actually in
// that kitchen. Everything after it descends in the order the morning
// happens:
//
//   pickup -> hair & makeup -> on set      when
//   location -> parking                    where
//   wardrobe -> scenes                     what
//   call sheet file                        the original
//
// PARKING IS NOT A FOOTNOTE. It sits with location, above wardrobe, because
// it is the difference between on-time and not — which is why every real call
// sheet puts it near the top. Sorting these fields alphabetically or by the
// pack's declaration order would bury it.
//
// ===================================================================
// AN ABSENT FIELD RENDERS NOTHING AT ALL
// ===================================================================
// The rest of this product says "not recorded" where a fact is missing,
// because elsewhere the absence is itself information — a role with no sides
// issued is a fact about the role.
//
// Here it is noise. The parent KNOWS what the call sheet said; a card of grey
// absences is harder to read at 5:40am than a card with four facts on it, and
// every "not recorded" pushes the one number they need further down. So this
// screen renders only what it has.
//
// THE PARSED FIELDS ARE WHAT YOU READ; THE FILE IS WHAT YOU SHOW THE AD.
// `call_sheet_ref` is offered at the bottom and never required — a call sheet
// nobody has parsed still has a pickup time written on it, and a card that
// demanded the file before showing anything would be useless on the morning
// it matters.

interface Props {
  tenantId: string;
  id: string;
  onBack: () => void;
  onOpenEngagement: (id: string) => void;
}

export function ShootDayCard(props: Props) {
  const { tenantId, id } = props;
  const recordsVersion = useRecordsVersion();
  const [record, setRecord] = useState<RecordRow | null>(null);
  const [engagement, setEngagement] = useState<RecordRow | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [sheetSay, setSheetSay] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    async function load() {
      try {
        const got = await getRecordWithLinks(tenantId, id);
        if (cancelled) return;
        setRecord(got.record);
        const engId = otherEnd(got.links, id, LINK.SHOOT_DAY_ENGAGEMENT);
        const eng = engId ? await getRecord(tenantId, engId).catch(() => null) : null;
        if (cancelled) return;
        setEngagement(eng);
        setLoaded(true);
        setFailure(null);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoaded(true);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, recordsVersion]);

  if (failure) {
    return (
      <div class="fsd">
        <div class="fsd__eyebrow">Shoot day</div>
        <div class="sp__say">{failure.headline}</div>
      </div>
    );
  }

  // A4's defect 2: claim nothing before looking. "One moment…" is not an
  // absence claim; "no pickup time recorded" would be, and would be false for
  // the half-second the read is in flight.
  if (!loaded || !record) {
    return (
      <div class="fsd">
        <div class="fsd__eyebrow">Shoot day</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const f = record.fields;
  // A TIME OF DAY, IN THE READER'S OWN LOCAL DAY. These are `datetime`
  // fields, and a pickup rendered an hour off is worse than no product at
  // all — the parent is not going to double-check it at 5:40am.
  const time = (v: unknown) => {
    if (typeof v !== 'string' || !v.trim()) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  };
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);

  const pickup = time(f.pickup_at);
  const hmu = time(f.hair_makeup_at);
  const onSet = time(f.on_set_at);
  // A `date` field is a CALENDAR DAY. Parsed as local Y/M/D, never through
  // UTC, which shows the day before for half of every day in Toronto.
  const date = (() => {
    const v = f.shoot_date;
    if (typeof v !== 'string' || !v.trim()) return null;
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(v.trim());
    const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  })();
  const label = text(f.day_label);
  const location = text(f.location);
  const parking = text(f.parking);
  const wardrobe = text(f.wardrobe);
  const scenes = text(f.scenes);
  const notes = text(f.notes);

  return (
    <div class="fsd">
      <div class="sp__crumb">
        {engagement ? (
          <>
            <button type="button" onClick={() => props.onOpenEngagement(engagement.id)}>
              the booking
            </button>
            <span class="sp__crumb-sep">›</span>
          </>
        ) : (
          <>
            <button type="button" onClick={props.onBack}>
              Back
            </button>
            <span class="sp__crumb-sep">›</span>
          </>
        )}
        <span class="sp__crumb-cur">this day</span>
      </div>

      <div class="fsd__eyebrow">{date || 'Shoot day'}</div>
      <h1 class="fsd__title">{label ?? date ?? 'Shoot day'}</h1>

      {/* THE NUMBER. Nothing else on this screen competes with it. */}
      {pickup ? (
        <div>
          <div class="fsd__pickup-k">Pickup</div>
          <div class="fsd__pickup">{pickup}</div>
        </div>
      ) : null}

      {/* The rest of the morning, in sequence. Rendered only if at least one
          of them exists — an empty bordered strip is a promise of a section
          the record does not have (A4's defect 6). */}
      {hmu || onSet ? (
        <div class="fsd__times">
          {hmu ? (
            <div>
              <div class="fsd__time-k">Hair &amp; makeup</div>
              <div class="fsd__time-v">{hmu}</div>
            </div>
          ) : null}
          {onSet ? (
            <div>
              <div class="fsd__time-k">On set</div>
              <div class="fsd__time-v">{onSet}</div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* WHERE — location and parking together, above wardrobe. */}
      {location ? (
        <div class="fsd__block">
          <div class="fsd__k">Location</div>
          <div class="fsd__v">{location}</div>
        </div>
      ) : null}
      {parking ? (
        <div class="fsd__block">
          <div class="fsd__k">Parking</div>
          <div class="fsd__v fsd__v--parking">{parking}</div>
        </div>
      ) : null}

      {/* WHAT */}
      {wardrobe ? (
        <div class="fsd__block">
          <div class="fsd__k">Wardrobe</div>
          <div class="fsd__v">{wardrobe}</div>
        </div>
      ) : null}
      {scenes ? (
        <div class="fsd__block">
          <div class="fsd__k">Scenes</div>
          <div class="fsd__v">{scenes}</div>
        </div>
      ) : null}
      {notes ? (
        <div class="fsd__block">
          <div class="fsd__k">Notes</div>
          <div class="fsd__v">{notes}</div>
        </div>
      ) : null}

      {/* A day with NOTHING on it yet. The one place this screen does speak
          about absence — because a card rendering literally nothing looks
          broken, and the parent needs the door to put the call sheet in. */}
      {!pickup && !hmu && !onSet && !location && !parking && !wardrobe && !scenes ? (
        <div class="sp__empty">Nothing from the call sheet yet.</div>
      ) : null}

      <div class="fsd__file">
        {/* THE ORIGINAL — the file this file's header promises ("the parsed
            fields are what you read; the file is what you show the AD").
            Opened through a fresh serving URL on tap: the presigned answer
            lives five minutes, and this card is exactly the screen someone
            leaves open. The window opens in the same tick as the tap so a
            popup blocker has nothing to object to. */}
        {f.call_sheet_ref ? (
          <button
            type="button"
            class="sp__edit"
            onClick={() => {
              setSheetSay(null);
              const w = window.open('about:blank', '_blank');
              getRecordFileUrl(tenantId, id, 'call_sheet_ref')
                .then((file) => {
                  if (w) w.location.href = file.url;
                  else window.location.href = file.url;
                })
                .catch((err) => {
                  w?.close();
                  setSheetSay(describeReadFailure(err).headline);
                });
            }}
          >
            The call sheet, as it arrived
          </button>
        ) : null}
        {sheetSay ? <div class="sp__say-soft">{sheetSay}</div> : null}
        <button
          type="button"
          class="sp__edit"
          onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.SHOOT_DAY, id })}
        >
          Edit this day
        </button>
      </div>
    </div>
  );
}
